import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea, Select } from 'flowbite-react';
import { v4 as uuidv4 } from 'uuid';
import { useDatabase } from '../../hooks/useDatabase';

const NativeResourceSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  managedByTeamId: z.string().optional(),
  /** 0 means "unlimited" (stored as null). */
  concurrencyLimit: z.coerce.number().int().min(0).optional(),
  cooldownDays: z.coerce.number().int().min(0).optional(),
});

type NativeResourceFormValues = z.infer<typeof NativeResourceSchema>;

interface Props {
  onSubmit: (command: any) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  onCreated?: (id: string) => void;
}

const NativeResourceForm: React.FC<Props> = ({ onSubmit, onClose, isOpen, onCreated }) => {
  if (!isOpen) return null;
  const { database } = useDatabase();
  const teams = database
    ? Object.entries(database.teams()).map(([id, t]: [string, any]) => ({ id, name: t.name() }))
    : [];

  const initialValues: NativeResourceFormValues = {
    name: '',
    description: '',
    managedByTeamId: '',
    concurrencyLimit: 0,
    cooldownDays: 0,
  };

  const handleSubmit = async (values: NativeResourceFormValues) => {
    const id = uuidv4();
    const concurrencyLimit =
      values.concurrencyLimit && values.concurrencyLimit > 0 ? values.concurrencyLimit : null;
    const cooldownDays =
      values.cooldownDays && values.cooldownDays > 0 ? values.cooldownDays : null;
    const command = {
      type: 'CreateItem',
      id,
      item: {
        type: 'Resource',
        value: {
          name: values.name,
          description: values.description ? values.description : null,
          managed_by_team: values.managedByTeamId ? values.managedByTeamId : null,
          concurrency_limit: concurrencyLimit,
          cooldown_days: cooldownDays,
        },
      },
    };
    await onSubmit(command);
    onCreated?.(id);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create Resource</h2>

        <Formik
          initialValues={initialValues}
          validationSchema={toFormikValidationSchema(NativeResourceSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="name">Name</Label>
                </div>
                <Field
                  as={TextInput}
                  id="name"
                  name="name"
                  autoFocus
                  placeholder="e.g. SEM Microscope, Conference Room A"
                />
                <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="description">Description</Label>
                </div>
                <Field
                  as={Textarea}
                  id="description"
                  name="description"
                  placeholder="What is this resource used for?"
                  rows={2}
                />
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="managedByTeamId">Managed by Team</Label>
                </div>
                <Field as={Select} id="managedByTeamId" name="managedByTeamId">
                  <option value="">— none —</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="mb-2">
                    <Label htmlFor="concurrencyLimit">Concurrency limit</Label>
                  </div>
                  <Field
                    as={TextInput}
                    type="number"
                    id="concurrencyLimit"
                    name="concurrencyLimit"
                    min={0}
                    placeholder="0 = unlimited"
                  />
                </div>
                <div>
                  <div className="mb-2">
                    <Label htmlFor="cooldownDays">Cooldown (days)</Label>
                  </div>
                  <Field
                    as={TextInput}
                    type="number"
                    id="cooldownDays"
                    name="cooldownDays"
                    min={0}
                    placeholder="0 = none"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Create Resource</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativeResourceForm;
